import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/client';
import DataTable from '@/components/common/DataTable';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  isApproved: boolean;
  user: { id: string; email: string; firstName: string | null; lastName: string | null };
  product: { id: string; name: string; slug: string };
  createdAt: string;
}

export default function ReviewsPage() {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      const params: Record<string, unknown> = { limit: 100 };
      if (filter === 'pending') params.isApproved = false;
      if (filter === 'approved') params.isApproved = true;

      const response = await adminApi.getReviews(params);
      setReviews(response.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (review: Review) => {
    try {
      await adminApi.updateReview(review.id, { isApproved: true });
      fetchReviews();
    } catch (err) {
      console.error('Error approving review:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    setIsSaving(true);

    try {
      await adminApi.deleteReview(selectedReview.id);
      setIsDeleteDialogOpen(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (err) {
      console.error('Error deleting review:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );

  const columns = [
    {
      key: 'product',
      label: t('reviews.product'),
      render: (review: Review) => (
        <div>
          <p className="font-medium">{review.product.name}</p>
        </div>
      ),
    },
    {
      key: 'user',
      label: t('reviews.user'),
      render: (review: Review) => (
        <div>
          <p className="text-sm">{review.user.firstName || review.user.email}</p>
        </div>
      ),
    },
    {
      key: 'rating',
      label: t('reviews.rating'),
      render: (review: Review) => renderStars(review.rating),
    },
    {
      key: 'comment',
      label: t('reviews.comment'),
      render: (review: Review) => (
        <p className="text-sm text-gray-600 max-w-xs truncate">{review.comment || '-'}</p>
      ),
    },
    {
      key: 'status',
      label: t('reviews.status'),
      render: (review: Review) => (
        <span className={`px-2 py-1 text-xs rounded-full ${review.isApproved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
          {review.isApproved ? t('reviews.approved') : t('reviews.pending')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (review: Review) => (
        <div className="flex gap-2">
          {!review.isApproved && (
            <button
              onClick={() => handleApprove(review)}
              className="p-1 text-gray-500 hover:text-green-600"
              title={t('reviews.approve')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
          <button
            onClick={() => { setSelectedReview(review); setIsDeleteDialogOpen(true); }}
            className="p-1 text-gray-500 hover:text-red-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('reviews.title')}</h1>
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
          {(['all', 'pending', 'approved'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap ${filter === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {f === 'all' ? 'All' : f === 'pending' ? t('reviews.pending') : t('reviews.approved')}
            </button>
          ))}
        </div>
      </div>

      <DataTable columns={columns} data={reviews} isLoading={isLoading} keyExtractor={(review) => review.id} />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('common.delete')}
        message={t('reviews.deleteConfirm')}
        isLoading={isSaving}
      />
    </div>
  );
}
